import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

const KACHING_RADIO_SELECTOR = 'input[name^="kaching-bundles-deal"]';
const KACHING_INITIALIZED_EVENT = "kaching-bundles-initialized";
const KACHING_INITIALIZATION_TIMEOUT = 5000;

/**
 * A custom element that displays a countdown timer for product offers.
 *
 * Supports two modes:
 * 1. Fixed date mode: Counts down to a specific end date (from data-end-date attribute)
 * 2. Recurring mode: Counts down to next Sunday at 11:59:59 PM LA timezone
 *
 * @typedef {object} Refs
 * @property {HTMLElement} daysContainer - Container for days display
 * @property {HTMLElement} daysElement - Element showing number of days
 * @property {HTMLElement} hoursElement - Element showing number of hours
 * @property {HTMLElement} minutesElement - Element showing number of minutes
 * @property {HTMLElement} secondsElement - Element showing number of seconds
 *
 * @extends {Component<Refs>}
 */
export class ProductOfferCountdownComponent extends Component {
  requiredRefs = ["hoursElement", "minutesElement", "secondsElement"];

  /** @type {number | undefined} */
  #timerInterval;

  /** @type {Date} */
  #offerEnds;

  /** @type {boolean} */
  #isFixedEndDate = false;

  /** @type {MutationObserver | undefined} */
  #kachingObserver;

  /** @type {number | undefined} */
  #kachingTimeout;

  /** @type {(() => void) | undefined} */
  #kachingReadyHandler;

  /** @type {HTMLElement | undefined} */
  #kachingBlock;

  /** @type {boolean} */
  #kachingStarted = false;

  /**
   * Called when the element is connected to the document's DOM.
   */
  async connectedCallback() {
    await super.connectedCallback();

    // Initialize countdown on page load
    this.#initializeCountdown();
    this.#initializeKachingDiscount();

    // Listen for variant changes via Horizon's native event
    const section = this.closest(".shopify-section");
    if (section) {
      section.addEventListener(
        ThemeEvents.variantUpdate,
        this.#handleVariantUpdate,
      );
    }
  }

  /**
   * Called when the element is removed from the DOM.
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up timer interval
    if (this.#timerInterval) {
      clearInterval(this.#timerInterval);
    }

    this.#stopWaitingForKaching();
    this.#kachingBlock?.removeEventListener(
      "deal-bar-selected",
      this.#handleKachingDealSelection,
    );
    document.removeEventListener("change", this.#handleKachingRadioChange);

    // Remove event listener
    const section = this.closest(".shopify-section");
    if (section) {
      section.removeEventListener(
        ThemeEvents.variantUpdate,
        this.#handleVariantUpdate,
      );
    }
  }

  /**
   * Handles variant update events - reinitializes countdown when variant changes.
   * Syncs mode/title with server-rendered HTML to keep flash-sale discount text accurate.
   * @private
   */
  #handleVariantUpdate = (event) => {
    const eventData = event.detail?.data;
    if (!eventData) return;
    if (!eventData.html) return;

    if (eventData.newProduct) {
      this.dataset.productId = eventData.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const updatedCountdown = this.#getUpdatedCountdownFromEvent(event);
    if (!updatedCountdown) {
      this.#hideCountdown();
      return;
    }

    this.#syncCountdownState(updatedCountdown);
    this.#initializeCountdown();
    this.#initializeKachingDiscount();

    if (this.#kachingStarted) this.#applyKachingDiscount();
  };

  /**
   * Finds the updated countdown element for this product in the variant update HTML.
   *
   * @private
   * @param {Event & { detail?: { data?: { html?: Document } } }} event
   * @returns {HTMLElement | null}
   */
  #getUpdatedCountdownFromEvent(event) {
    const productId = this.dataset.productId;
    const html = event.detail?.data?.html;

    if (!productId || !html) return null;

    return html.querySelector(
      `product-offer-countdown[data-product-id="${productId}"]`,
    );
  }

  /**
   * Syncs countdown data attributes and title from server-rendered HTML.
   *
   * @private
   * @param {HTMLElement} updatedCountdown
   */
  #syncCountdownState(updatedCountdown) {
    this.style.removeProperty("display");

    this.dataset.countdownMode =
      updatedCountdown.dataset.countdownMode || "none";

    if (updatedCountdown.dataset.originalTitle) {
      this.dataset.originalTitle = updatedCountdown.dataset.originalTitle;
    } else {
      delete this.dataset.originalTitle;
    }

    if (updatedCountdown.dataset.endDate) {
      this.dataset.endDate = updatedCountdown.dataset.endDate;
    } else {
      delete this.dataset.endDate;
    }

    for (const attribute of [
      "syncKachingBundleDiscount",
      "variantPrice",
      "variantCompareAtPrice",
    ]) {
      if (updatedCountdown.dataset[attribute]) {
        this.dataset[attribute] = updatedCountdown.dataset[attribute];
      } else {
        delete this.dataset[attribute];
      }
    }

    const currentTitle = this.querySelector('[ref="countdownTitle"]');
    const updatedTitle = updatedCountdown.querySelector(
      '[ref="countdownTitle"]',
    );
    if (currentTitle && updatedTitle) {
      currentTitle.textContent = updatedTitle.textContent?.trim() || "";
    }
  }

  /**
   * Stops the timer and hides the component.
   * @private
   */
  #hideCountdown() {
    if (this.#timerInterval) {
      clearInterval(this.#timerInterval);
      this.#timerInterval = undefined;
    }

    this.#displayZeros();
    this.style.display = "none";
  }

  /** Reveals a Kaching-synced countdown once its title is safe to show. */
  #showCountdown() {
    this.style.removeProperty("visibility");
    this.style.removeProperty("display");
    if (!this.#timerInterval) this.#initializeCountdown();
  }

  /** Starts Kaching discount syncing for explicitly opted-in countdowns. */
  #initializeKachingDiscount() {
    if (this.dataset.syncKachingBundleDiscount !== "true") return;
    if (this.#kachingStarted) return;

    const isReady = () =>
      document.querySelector("kaching-bundles-block[deal-block]") != null &&
      document.querySelector(KACHING_RADIO_SELECTOR) != null;

    const startIfReady = () => {
      if (!isReady() || this.#kachingStarted) return;

      this.#kachingStarted = true;
      this.#stopWaitingForKaching();
      this.#kachingBlock = document.querySelector(
        "kaching-bundles-block[deal-block]",
      );
      this.#kachingBlock?.addEventListener(
        "deal-bar-selected",
        this.#handleKachingDealSelection,
      );
      document.addEventListener("change", this.#handleKachingRadioChange);
      this.#applyKachingDiscount();
    };

    if (isReady()) {
      startIfReady();
      return;
    }

    this.#kachingReadyHandler = startIfReady;
    window.addEventListener(
      KACHING_INITIALIZED_EVENT,
      this.#kachingReadyHandler,
    );
    this.#kachingObserver = new MutationObserver(startIfReady);
    this.#kachingObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    this.#kachingTimeout = window.setTimeout(() => {
      this.#stopWaitingForKaching();
      this.#restoreServerCountdown();
    }, KACHING_INITIALIZATION_TIMEOUT);
    startIfReady();
  }

  /** Cleans up the short-lived Kaching initialization listeners. */
  #stopWaitingForKaching = () => {
    if (this.#kachingReadyHandler) {
      window.removeEventListener(
        KACHING_INITIALIZED_EVENT,
        this.#kachingReadyHandler,
      );
      this.#kachingReadyHandler = undefined;
    }
    this.#kachingObserver?.disconnect();
    this.#kachingObserver = undefined;
    if (this.#kachingTimeout) window.clearTimeout(this.#kachingTimeout);
    this.#kachingTimeout = undefined;
  };

  /** Recomputes after Kaching's documented deal selection event. */
  #handleKachingDealSelection = () => {
    queueMicrotask(() => this.#applyKachingDiscount());
  };

  /** Recomputes after the app's checked deal radio changes. */
  #handleKachingRadioChange = (event) => {
    const target = event.target;
    if (
      target instanceof HTMLInputElement &&
      target.name.startsWith("kaching-bundles-deal")
    ) {
      this.#applyKachingDiscount();
    }
  };

  /** Finds the Kaching deal bar for the currently selected radio. */
  #getSelectedKachingBar() {
    const selectedId = document.querySelector(
      `${KACHING_RADIO_SELECTOR}:checked`,
    )?.value;
    if (!selectedId) return null;

    const dealBlock = this.#kachingBlock?.getAttribute("deal-block");
    if (dealBlock) {
      try {
        const config = JSON.parse(dealBlock);
        const configuredBar = config.dealBars?.find(
          (bar) => String(bar?.id) === selectedId,
        );
        if (configuredBar) return configuredBar;
      } catch {
        // The legacy global below remains a safe fallback for malformed app data.
      }
    }

    for (const deal of window.__kachingCartBundlesDeals ?? []) {
      for (const bar of deal?.dealBars ?? []) {
        if (String(bar?.id) === selectedId) return bar;
      }
    }

    return null;
  }

  /** Reads the product data Kaching provides for gifts in the current widget. */
  #getKachingOtherProducts() {
    const otherProducts = this.#kachingBlock?.getAttribute("other-products");
    if (!otherProducts) return null;

    try {
      const products = JSON.parse(otherProducts);
      return Array.isArray(products) ? products : null;
    } catch {
      return null;
    }
  }

  /**
   * Returns the retail value of gifts Kaching includes in the compare-at price.
   * An unresolved included gift returns null so callers can use hoodie-only value.
   */
  #getIncludedGiftValue(selectedBar) {
    const freeGifts = Array.isArray(selectedBar.freeGifts)
      ? selectedBar.freeGifts
      : [];
    const includedGifts = freeGifts.filter(
      (gift) => gift?.includeInCompareAt === true,
    );
    if (includedGifts.length === 0) return 0;

    const otherProducts = this.#getKachingOtherProducts();
    if (!otherProducts) return null;

    let totalGiftValue = 0;
    for (const gift of includedGifts) {
      const productId = String(gift.productGID ?? "")
        .split("/")
        .pop();
      const product = otherProducts.find(
        (otherProduct) => String(otherProduct?.id) === productId,
      );
      if (!product) return null;

      const variantGIDs = Array.isArray(gift.variantGIDs)
        ? gift.variantGIDs
        : [];
      const allowedVariantIds = variantGIDs.map((variantId) =>
        String(variantId).split("/").pop(),
      );
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const selectedVariantId = String(product.selectedVariantId ?? "");
      const selectedVariant = variants.find(
        (variant) =>
          String(variant?.id) === selectedVariantId &&
          (allowedVariantIds.length === 0 ||
            allowedVariantIds.includes(String(variant?.id))),
      );
      const allowedVariant = variants.find((variant) =>
        allowedVariantIds.includes(String(variant?.id)),
      );
      const defaultVariant =
        allowedVariantIds.length === 0
          ? (variants.find((variant) => variant?.availableForSale) ??
            variants[0])
          : null;
      const price = Number(
        (selectedVariant ?? allowedVariant ?? defaultVariant)?.price,
      );
      const quantity = Number(gift.quantity) || 1;
      if (!Number.isFinite(price) || price < 0 || quantity <= 0) return null;

      totalGiftValue += price * quantity;
    }

    return totalGiftValue;
  }

  /** Updates the countdown title from the selected Kaching bundle tier. */
  #applyKachingDiscount() {
    const selectedBar = this.#getSelectedKachingBar();
    if (!selectedBar) {
      this.#restoreServerCountdown();
      return;
    }

    const variantPrice = Number(this.dataset.variantPrice);
    const compareAtPrice = Number(this.dataset.variantCompareAtPrice) || 0;
    const quantity = Number(selectedBar.quantity) || 1;
    if (!Number.isFinite(variantPrice) || variantPrice <= 0 || quantity <= 0) {
      this.#restoreServerCountdown();
      return;
    }

    const discountType = selectedBar.discount?.type ?? selectedBar.discountType;
    const discountValue =
      selectedBar.discount?.value ?? selectedBar.discountValue;

    let bundlePrice = variantPrice * quantity;
    if (discountType === "specific") {
      const configuredBundlePrice = Number(discountValue);
      if (
        !Number.isFinite(configuredBundlePrice) ||
        configuredBundlePrice < 0
      ) {
        this.#restoreServerCountdown();
        return;
      }
      bundlePrice = Math.round(configuredBundlePrice * 100);
    } else if (selectedBar.discount != null || discountType != null) {
      this.#restoreServerCountdown();
      return;
    }

    const hoodieListValue = Math.max(variantPrice, compareAtPrice) * quantity;
    const giftValue = this.#getIncludedGiftValue(selectedBar) ?? 0;
    const totalValue = hoodieListValue + giftValue;
    const discountPercent = Math.round(
      ((totalValue - bundlePrice) / totalValue) * 100,
    );

    if (!Number.isFinite(discountPercent) || discountPercent <= 0) {
      this.#hideCountdown();
      return;
    }

    const title = this.querySelector('[ref="countdownTitle"]');
    if (title) title.textContent = `${discountPercent}% OFF`;

    this.#showCountdown();
  }

  /** Restores the server title when Kaching cannot provide a usable tier. */
  #restoreServerCountdown() {
    const title = this.querySelector('[ref="countdownTitle"]');
    if (title) title.textContent = this.dataset.originalTitle || "";

    this.#showCountdown();
  }

  /**
   * Calculates the next Sunday at 11:59:59 PM in LA timezone.
   * Accounts for daylight saving time and timezone differences.
   *
   * @private
   * @returns {Date} The next Sunday 11:59:59 PM in user's browser timezone
   */
  #getNextSundayMidnight() {
    const now = new Date();

    // Get LA time components using locale string
    const laTimeStr = now.toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
    });

    // Parse LA time string: "m/d/yyyy, h:m:s"
    const [datePart, timePart] = laTimeStr.split(", ");
    const [month, day, year] = datePart.split("/").map(Number);
    const [hour, minute, second] = timePart.split(":").map(Number);

    // Create date object representing current LA time
    const laDate = new Date(year, month - 1, day, hour, minute, second);
    const currentDay = laDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Calculate days until next Sunday (target: 11:59:59 PM)
    let daysToAdd;
    if (currentDay === 0) {
      // It's Sunday
      // If it's past 11:59:59 PM, go to next Sunday
      if (hour === 23 && minute === 59 && second >= 59) {
        daysToAdd = 7;
      } else {
        daysToAdd = 0; // It's still this Sunday
      }
    } else {
      // Calculate days until next Sunday
      daysToAdd = 7 - currentDay;
    }

    // Set target date to next Sunday at 11:59:59 PM (999ms for precision)
    const targetDate = new Date(
      year,
      month - 1,
      day + daysToAdd,
      23,
      59,
      59,
      999,
    );

    // Convert target date back to user's timezone
    const msOffset =
      targetDate.getTime() -
      laDate.getTime() +
      now.getTime() -
      new Date(year, month - 1, day, hour, minute, second).getTime();

    return new Date(now.getTime() + msOffset);
  }

  /**
   * Initializes or reinitializes the countdown timer.
   * Clears any existing timer and starts a new one.
   *
   * @private
   */
  #initializeCountdown() {
    // Clean up existing timer
    if (this.#timerInterval) {
      clearInterval(this.#timerInterval);
      this.#timerInterval = undefined;
    }

    // Check for custom end date (fixed date mode)
    const customEndDate = this.dataset.endDate;
    if (customEndDate) {
      this.#offerEnds = new Date(customEndDate);
      this.#isFixedEndDate = true;
    } else {
      // Calculate next Sunday deadline (recurring mode)
      this.#offerEnds = this.#getNextSundayMidnight();
      this.#isFixedEndDate = false;
    }

    // Update display immediately
    this.#updateCountdown();

    // Update display every second
    this.#timerInterval = setInterval(() => this.#updateCountdown(), 1000);
  }

  /**
   * Updates the countdown display with current time remaining.
   * For recurring mode: auto-resets when countdown reaches zero.
   * For fixed date mode: hides component when expired.
   *
   * @private
   */
  #updateCountdown() {
    const now = new Date();
    let timeDiff = this.#offerEnds - now;

    // Handle countdown completion
    if (timeDiff <= 0) {
      if (this.#isFixedEndDate) {
        // Fixed date mode: show zeros and hide component
        this.#displayZeros();
        if (this.#timerInterval) {
          clearInterval(this.#timerInterval);
          this.#timerInterval = undefined;
        }
        this.style.display = "none";
        return;
      } else {
        // Recurring mode: recalculate next Sunday
        this.#offerEnds = this.#getNextSundayMidnight();
        timeDiff = this.#offerEnds - now;
      }
    }

    // Calculate time components
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

    // Update DOM elements safely
    const {
      daysContainer,
      daysElement,
      hoursElement,
      minutesElement,
      secondsElement,
    } = this.refs;

    // Show/hide days container based on whether there are days remaining
    if (days > 0 && daysContainer && daysElement) {
      daysContainer.style.display = "inline";
      daysElement.textContent = days;
    } else if (daysContainer) {
      daysContainer.style.display = "none";
    }

    // Update time values with zero padding
    if (hoursElement)
      hoursElement.textContent = hours.toString().padStart(2, "0");
    if (minutesElement)
      minutesElement.textContent = minutes.toString().padStart(2, "0");
    if (secondsElement)
      secondsElement.textContent = seconds.toString().padStart(2, "0");
  }

  /**
   * Displays zeros for all countdown elements.
   * @private
   */
  #displayZeros() {
    const {
      daysContainer,
      daysElement,
      hoursElement,
      minutesElement,
      secondsElement,
    } = this.refs;

    if (daysContainer) daysContainer.style.display = "none";
    if (daysElement) daysElement.textContent = "0";
    if (hoursElement) hoursElement.textContent = "00";
    if (minutesElement) minutesElement.textContent = "00";
    if (secondsElement) secondsElement.textContent = "00";
  }
}

if (!customElements.get("product-offer-countdown")) {
  customElements.define(
    "product-offer-countdown",
    ProductOfferCountdownComponent,
  );
}
